import { externalAccessHeaders } from '@/Utils/externalAccessHeaders';
import { HEALTHCHECK_PATH } from '@/services/healthClient';
import { useConnectivityStore } from '@/stores/connectivity';
import * as Sentry from '@sentry/vue';
import axios from 'axios';

const MAX_CSRF_RETRY_COUNT = 1;
const MAX_NETWORK_RETRY_COUNT = 2;
const CSRF_RETRY_DELAY = 500;
const LOGIN_PATH = '/login';
const CONNECTIVITY_EXEMPT_PATHS = [HEALTHCHECK_PATH, '/sanctum/csrf-token'];

let isRedirectingToLogin = false;

function redirectToLogin() {
    if (isRedirectingToLogin) {
        return;
    }

    isRedirectingToLogin = true;
    window.location.assign(LOGIN_PATH);
}

function sanitizeUrl(url) {
    if (!url || typeof url !== 'string') {
        return url;
    }

    const queryIndex = url.indexOf('?');
    const hashIndex = url.indexOf('#');

    if (queryIndex === -1 && hashIndex === -1) {
        return url;
    }

    const firstSpecialChar = [queryIndex, hashIndex].filter((i) => i !== -1).reduce((min, i) => Math.min(min, i), Infinity);

    return url.substring(0, firstSpecialChar);
}

function logRequestToSentry(config) {
    const sanitizedUrl = sanitizeUrl(config?.url);

    Sentry.addBreadcrumb({
        category: 'http',
        message: `Request: ${config.method?.toUpperCase()} ${sanitizedUrl}`,
        level: 'info',
        data: {
            url: sanitizedUrl,
            method: config.method,
        },
    });
}

function logResponseErrorToSentry(config, response) {
    const sanitizedUrl = sanitizeUrl(config?.url);

    Sentry.addBreadcrumb({
        category: 'http',
        message: `Response Error: ${response.status} ${config?.method?.toUpperCase()} ${sanitizedUrl}`,
        level: 'error',
        data: {
            url: sanitizedUrl,
            method: config?.method,
            status: response.status,
            statusText: response.statusText,
        },
    });
}

function logNetworkErrorToSentry(config, errorCode, errorMessage) {
    const sanitizedUrl = sanitizeUrl(config?.url);

    const errorDetails = {
        url: sanitizedUrl,
        method: config?.method,
        errorCode,
        errorMessage,
    };

    Sentry.addBreadcrumb({
        category: 'http',
        message: `Network Error: ${config?.method?.toUpperCase()} ${sanitizedUrl}`,
        level: 'error',
        data: errorDetails,
    });

    return errorDetails;
}

function logRetryToSentry(currentRetryCount, maxRetryCount) {
    Sentry.addBreadcrumb({
        category: 'http',
        message: `Retrying request (${currentRetryCount + 1}/${maxRetryCount})`,
        level: 'info',
    });
}

function calculateRetryDelay(retryCount) {
    return Math.min(1000 * Math.pow(2, retryCount), 5000);
}

function isConnectivityExempt(config) {
    const url = config?.url;
    if (!url || typeof url !== 'string') {
        return false;
    }
    return CONNECTIVITY_EXEMPT_PATHS.some((path) => url.includes(path));
}

function notifyConnectivityLost(reason) {
    try {
        useConnectivityStore().markOffline(reason);
    } catch {
        // Pinia not yet active or store unavailable — gate will catch up on next boot path.
    }
}

function notifyNetworkFlake() {
    try {
        useConnectivityStore().notifyNetworkFlake();
    } catch {
        // Pinia not yet active or store unavailable — flake signal is best-effort.
    }
}

function shouldRetryNetworkError(config, currentRetryCount) {
    if (!config || currentRetryCount >= MAX_NETWORK_RETRY_COUNT) {
        return false;
    }

    const method = (config.method || 'get').toLowerCase();
    const isSafeMethod = ['get', 'head', 'options'].includes(method);

    return isSafeMethod || config.allowNetworkRetry === true;
}

async function handleNetworkError(err) {
    const { config } = err;

    if (axios.isCancel?.(err) || err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || config?.signal?.aborted) {
        throw err;
    }

    const currentRetryCount = config?.networkRetryCount || 0;

    const errorDetails = logNetworkErrorToSentry(config, err.code, err.message);

    if (!isConnectivityExempt(config)) {
        notifyNetworkFlake();
    }

    if (shouldRetryNetworkError(config, currentRetryCount)) {
        config.networkRetryCount = currentRetryCount + 1;

        const retryDelay = calculateRetryDelay(currentRetryCount);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));

        logRetryToSentry(currentRetryCount, MAX_NETWORK_RETRY_COUNT);

        return axios(config);
    }

    err.message = `Network request failed: ${err.message}`;
    err.requestDetails = errorDetails;
    err.code = err.code || 'ERR_NETWORK';

    if (!isConnectivityExempt(config)) {
        notifyConnectivityLost(err.message);
    }

    throw err;
}

async function handleCsrfTokenExpired(config, currentRetryCount) {
    if (currentRetryCount >= MAX_CSRF_RETRY_COUNT) {
        return null;
    }

    try {
        await axios.get('/sanctum/csrf-token');
        config.retryCount = currentRetryCount + 1;

        await new Promise((resolve) => setTimeout(resolve, CSRF_RETRY_DELAY));

        return axios(config);
    } catch (csrfErr) {
        throw csrfErr;
    }
}

async function handleHttpStatusError(err) {
    const { config, response } = err;

    if (!config) {
        throw err;
    }

    if (config.url?.includes('/sanctum/csrf-token')) {
        throw err;
    }

    const currentRetryCount = config.retryCount || 0;

    switch (response.status) {
        case 419: {
            const retryResponse = await handleCsrfTokenExpired(config, currentRetryCount);
            if (retryResponse) {
                return retryResponse;
            }
            break;
        }
        case 401:
            redirectToLogin();
            return new Promise(() => {});
    }

    throw err;
}

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

axios.interceptors.request.use(
    (config) => {
        logRequestToSentry(config);

        // Same-origin only: never send the external token to the cross-origin target of an upload.
        const externalHeaders = externalAccessHeaders(config.url);
        Object.keys(externalHeaders).forEach((header) => {
            config.headers[header] = externalHeaders[header];
        });

        return config;
    },
    (error) => Promise.reject(error),
);

axios.interceptors.response.use(
    (response) => response,
    async (err) => {
        if (isRedirectingToLogin) {
            return new Promise(() => {});
        }

        if (!err.response) {
            return handleNetworkError(err);
        }

        if (err.response.status >= 400) {
            logResponseErrorToSentry(err.config, err.response);
        }

        return handleHttpStatusError(err);
    },
);

export const $http = axios;
